/**
 * PostChatReflection Component (Dual-Mode Support)
 *
 * Intermediate screen between Communication/Learning Phase and Comprehension Quiz.
 * Captures participants' reflections on the teaching/learning experience.
 *
 * DYAD Mode:
 * - Teacher (marker=0): Self-assess their teaching + predict partner's quiz score (0-6)
 * - Student (marker>0): Rate their confidence (2-item scale) + rate partner's teaching (clarity, helpfulness)
 *   (own-score prediction moved to the pre/post quiz rounds)
 *
 * SINGLE Mode:
 * - Learner: Self-assess their understanding (own-score prediction moved to the quiz rounds)
 *
 * Data Storage:
 * - Teacher: no role-specific battery here anymore — the partner-score prediction
 *   moved to the Post-Learning Quiz pre-quiz screen (ComprehensionQuiz),
 *   saved as player.set("partner_quiz_prediction", 0..N)
 * - Teacher: player.set("postChat_state_confidence", { conf_understand, conf_answer })
 * - Student: player.set("postChatReflection", { explanationClarity, explanationHelpfulness })
 * - Student: player.set("postChat_state_confidence", { conf_understand, conf_answer })
 * - Student: player.set("postChat_partner_expertise", { value }) — perceived partner
 *   expertise, pre-quiz. Same item/scale as ExitSurvey `partnerExpertise` so the
 *   R7 (pre-quiz) → exit (post-quiz) change in credibility is measurable.
 * - Learner: player.set("selfReflection", { ownUnderstanding, readinessForQuiz })
 * - Learner: player.set("postReflection_state_confidence", { conf_understand, conf_answer })
 *   (distinct key — "postLearning_state_confidence" belongs to Round 3 / RoleInstructions)
 */

import React, { useState } from "react";
import { usePlayer, useGame } from "@empirica/core/player/classic/react";
import { ConfidenceScale, isConfidenceComplete, getEmptyConfidenceState } from "./ConfidenceScale";
import { ObjectivesChecklist, parseObjectives, buildCoverageRecord } from "./ObjectivesChecklist";

export function PostChatReflection({ next }) {
  const player = usePlayer();
  const game = useGame();

  const marker = player.get("marker");
  const config = game.get("config");
  const topicName = config?.name || "the topic";
  const gameMode = game.get("gameMode") || "dyad";
  const isSingleMode = gameMode === "single";
  const condition = game.get("condition") || "pdf"; // ai, pdf, ai-fixed, ai-pdf, web

  // Determine role based on game mode
  const assignedRole = player.get("assignedRole");
  const isLearner = isSingleMode || assignedRole === "Learner";
  const isTeacher = !isSingleMode && marker === 0;
  const isStudent = !isSingleMode && marker > 0;

  // Teacher has no rating battery here anymore: the partner-score prediction moved
  // to the Post-Learning Quiz pre-quiz screen (ComprehensionQuiz).
  const [teacherAssessment, setTeacherAssessment] = useState({});

  // Student has no rating battery here anymore (clarity/helpfulness removed to
  // avoid duplicating the single explanation question, below).
  const [studentReflection, setStudentReflection] = useState({});

  // Learner's self-reflection state (single mode)
  const [learnerReflection, setLearnerReflection] = useState({
    ownUnderstanding: null,
    readinessForQuiz: null,
  });

  // State confidence (2-item scale). Score predictions live in the quiz rounds.
  const [stateConfidence, setStateConfidence] = useState(getEmptyConfidenceState());

  // Post-teaching point of the explanation-confidence trajectory (dyad only):
  // Teacher rates their own ability to explain; Student rates how well their
  // partner explained. Learner (single) has no teaching interaction → skipped.
  const [explainRating, setExplainRating] = useState(null);

  // Perceived partner expertise (Student only, dyad): source-credibility read taken
  // right after the chat, BEFORE the quiz can correct it. Same item/scale as the
  // exit-survey `partnerExpertise`, so the R7→exit change is measurable.
  const [partnerExpertise, setPartnerExpertise] = useState(null);

  // Objectives coverage after the chat (dyad only): Teacher rates what they covered,
  // Student rates what their partner covered. Same 5 objectives, role-specific source.
  const objectives = parseObjectives(isTeacher ? config?.instructionExplainer : config?.instructionLearner);
  const [objectivesCovered, setObjectivesCovered] = useState([]);
  const toggleObjective = (idx) =>
    setObjectivesCovered((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );

  // Check if all questions are answered
  const roleQuestionsComplete = isLearner
    ? Object.values(learnerReflection).every((v) => v !== null)
    : isTeacher
    ? Object.values(teacherAssessment).every((v) => v !== null)
    : Object.values(studentReflection).every((v) => v !== null);
  const confidenceComplete = isConfidenceComplete(stateConfidence, []);
  const explainComplete = isLearner || explainRating !== null;
  // Student additionally rates partner expertise (R7 source-credibility read).
  const expertiseComplete = !isStudent || partnerExpertise !== null;
  const isComplete = roleQuestionsComplete && confidenceComplete && explainComplete && expertiseComplete;

  const handleTeacherChange = (question, value) => {
    setTeacherAssessment((prev) => ({ ...prev, [question]: value }));
  };

  const handleStudentChange = (question, value) => {
    setStudentReflection((prev) => ({ ...prev, [question]: value }));
  };

  const handleLearnerChange = (question, value) => {
    setLearnerReflection((prev) => ({ ...prev, [question]: value }));
  };

  const handleStateConfidenceChange = (key, value) => {
    setStateConfidence((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (!isComplete) return;

    // Save state confidence (2-item scale) for all roles.
    // Learners use a DISTINCT key: "postLearning_state_confidence" is already
    // written in Round 3 (RoleInstructions, incl. predictedOwnScore) — reusing
    // it here overwrote that record and destroyed the prediction.
    const confidenceKey = isLearner ? "postReflection_state_confidence" : "postChat_state_confidence";
    player.set(confidenceKey, {
      ...stateConfidence,
      topicId: config?.id,
      timestamp: new Date().toISOString(),
    });

    if (isLearner) {
      // Single mode: Save learner's self-reflection
      player.set("selfReflection", {
        ...learnerReflection,
        topicId: config?.id,
        timestamp: new Date().toISOString(),
      });
    } else if (isTeacher) {
      // No teacher-specific battery here anymore (partner prediction moved to the
      // Post-Learning Quiz pre-quiz screen). Confidence, the post-teaching explain
      // item, and objectives coverage are saved below.
    } else {
      player.set("postChatReflection", {
        ...studentReflection,
        topicId: config?.id,
        timestamp: new Date().toISOString(),
      });
    }

    // Post-teaching explanation-confidence item (dyad only) — the trajectory's
    // third point for the Teacher, second for the Student.
    if (!isLearner) {
      const explainKey = isTeacher ? "postTeaching_explain_ability" : "postTeaching_explain_evaluation";
      player.set(explainKey, {
        value: Number(explainRating),
        topicId: config?.id,
        timestamp: new Date().toISOString(),
      });
    }

    // Student only: perceived partner expertise, pre-quiz (mirrors exit-survey item)
    if (isStudent) {
      player.set("postChat_partner_expertise", {
        value: Number(partnerExpertise),
        topicId: config?.id,
        timestamp: new Date().toISOString(),
      });
    }

    // Objectives coverage after the chat (dyad only): which objectives were covered
    if (!isLearner) {
      player.set("postChat_objectives_covered", {
        ...buildCoverageRecord(objectives, objectivesCovered),
        role: isTeacher ? "teacher" : "student",
        topicId: config?.id,
        timestamp: new Date().toISOString(),
      });
    }
    next();
  };

  // Teacher: no rating battery here anymore — the partner-score prediction moved
  // to the Post-Learning Quiz pre-quiz screen. The post-teaching explanation item
  // (below) is rendered separately.
  const teacherQuestions = [];

  // Student: no rating battery here (clarity/helpfulness removed as duplicates).
  const studentQuestions = [];

  // Learner questions (single mode - self-reflection).
  // Score prediction moved to the quiz rounds (pre/post), so no prediction here.
  const learnerQuestions = [
    {
      key: "ownUnderstanding",
      label: "How well do you feel you understand this topic now?",
      lowLabel: "Not at all",
      highLabel: "Very well",
    },
    {
      key: "readinessForQuiz",
      label: "How ready do you feel to take a quiz on this topic?",
      lowLabel: "Not ready",
      highLabel: "Very ready",
    },
  ];

  const questions = isLearner ? learnerQuestions : (isTeacher ? teacherQuestions : studentQuestions);
  const answers = isLearner ? learnerReflection : (isTeacher ? teacherAssessment : studentReflection);
  const handleChange = isLearner ? handleLearnerChange : (isTeacher ? handleTeacherChange : handleStudentChange);

  // Role-specific styling
  const roleColor = isLearner ? "violet" : (isTeacher ? "blue" : "emerald");
  const gradientClass = isLearner
    ? "bg-gradient-to-r from-violet-600 to-purple-600"
    : isTeacher
    ? "bg-gradient-to-r from-blue-600 to-indigo-600"
    : "bg-gradient-to-r from-emerald-600 to-teal-600";
  const roleName = isLearner ? "Learner" : (isTeacher ? "Teacher" : "Student");

  // Header background color (solid, not gradient)
  const headerBgClass = isLearner
    ? "bg-violet-600"
    : isTeacher
    ? "bg-blue-600"
    : "bg-emerald-600";

  const buttonClass = isLearner
    ? "bg-violet-600 hover:bg-violet-700"
    : isTeacher
    ? "bg-blue-600 hover:bg-blue-700"
    : "bg-emerald-600 hover:bg-emerald-700";

  return (
    <div className="h-full overflow-y-auto bg-gray-50 py-6 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className={`${headerBgClass} rounded-xl p-4 mb-6 text-white text-center`}>
          <span className="text-sm font-medium uppercase tracking-wide opacity-80">{roleName}</span>
          <h2 className="text-xl font-bold mt-1">{topicName}</h2>
          <p className="text-sm opacity-80 mt-1">
            {isLearner ? "Reflect on Your Learning" : (isTeacher ? "Reflect on Your Teaching" : "Reflect on Your Learning")}
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* State Confidence Scale (2-item; predictions live in the quiz rounds) */}
          <div className="mb-6 space-y-4">
            <h4 className="text-base font-semibold text-gray-800">Rate Your Confidence</h4>
            <ConfidenceScale
              values={stateConfidence}
              onChange={handleStateConfidenceChange}
              color={roleColor}
              topicName={topicName}
              startNumber={1}
              showHeader={false}
              includePrediction={false}
            />
          </div>

          {/* Role-Specific Questions (only rendered if the role has any) */}
          {questions.length > 0 && (
            <div className="mb-6 space-y-4">
              <h4 className="text-base font-semibold text-gray-800">
                {isLearner
                  ? "Rate Your Understanding"
                  : isTeacher
                  ? "Your Prediction"
                  : "Rate Your Partner"}
              </h4>
              {questions.map((q, index) => (
                <RatingQuestion
                  key={q.key}
                  number={(isStudent ? 4 : 3) + index}
                  label={q.label}
                  lowLabel={q.lowLabel}
                  highLabel={q.highLabel}
                  value={answers[q.key]}
                  onChange={(v) => handleChange(q.key, v)}
                  color={roleColor}
                  scale={q.scale}
                />
              ))}
            </div>
          )}

          {/* Post-teaching explanation-confidence item (dyad only) */}
          {!isLearner && (
            <div className="mb-6 space-y-4">
              <h4 className="text-base font-semibold text-gray-800">
                {isTeacher ? "Explaining This Topic" : "Your Partner's Explanation"}
              </h4>
              <RatingQuestion
                number={(isStudent ? 4 : 3) + questions.length}
                label={isTeacher
                  ? "How well did you explain this topic to your partner?"
                  : "How well did your partner explain this topic to you?"}
                lowLabel="Not at all well"
                highLabel="Extremely well"
                value={explainRating}
                onChange={(v) => setExplainRating(v)}
                color={roleColor}
              />
            </div>
          )}

          {/* Perceived partner expertise (Student only) — pre-quiz credibility read,
              same item/scale as the exit survey's partnerExpertise. */}
          {isStudent && (
            <div className="mb-6 space-y-4">
              <h4 className="text-base font-semibold text-gray-800">
                Your Partner
              </h4>
              <RatingQuestion
                number={4 + questions.length + 1}
                label="How knowledgeable did your partner seem?"
                lowLabel="Not at all"
                highLabel="Very knowledgeable"
                value={partnerExpertise}
                onChange={(v) => setPartnerExpertise(v)}
                color={roleColor}
              />
            </div>
          )}

          {/* Objectives coverage after the chat (dyad only) */}
          {!isLearner && objectives.length > 0 && (
            <div className="mb-6 space-y-2">
              <h4 className="text-base font-semibold text-gray-800">Topics Covered</h4>
              <ObjectivesChecklist
                objectives={objectives}
                covered={objectivesCovered}
                onToggle={toggleObjective}
                color={roleColor}
                instructions={
                  isTeacher
                    ? "Which of these did you cover in your explanation? Tick all that apply."
                    : "Which of these did your partner cover? Tick all that apply."
                }
              />
            </div>
          )}

          {/* Quiz reminder */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
            <p className="text-sm text-amber-800">
              <strong>Next:</strong> You will complete a comprehension quiz on this topic.
            </p>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={!isComplete}
            className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-200 ${
              isComplete ? buttonClass : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            {isComplete ? "Continue to Quiz" : "Please answer all questions"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * RatingQuestion Component
 * Reusable scale rating question (0-6 default, or 0-12 for predictions)
 */
function RatingQuestion({ number, label, lowLabel, highLabel, value, onChange, color = "blue", scale }) {
  const colorClasses = {
    blue: {
      selected: "bg-blue-600 text-white shadow-md scale-110",
      unselected: "bg-gray-100 text-gray-700 hover:bg-gray-200",
    },
    emerald: {
      selected: "bg-emerald-600 text-white shadow-md scale-110",
      unselected: "bg-gray-100 text-gray-700 hover:bg-gray-200",
    },
    violet: {
      selected: "bg-violet-600 text-white shadow-md scale-110",
      unselected: "bg-gray-100 text-gray-700 hover:bg-gray-200",
    },
  };

  // Prediction questions use a 0..scale range (scale = quiz-half size, e.g. 6 or
  // 9); rating items default to 0-6. Wider-than-6 scales get compact styling and
  // drop the end labels so the row still fits.
  const scaleMax = scale || 6;
  const isWideScale = scaleMax > 6;
  const scaleValues = Array.from({ length: scaleMax + 1 }, (_, v) => v);

  return (
    <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
      <label className="block text-sm font-medium text-gray-800">
        {number}. {label}
      </label>
      <div className="flex items-center gap-2">
        {!isWideScale && (
          <span className="text-xs text-gray-500 w-28 text-right">{lowLabel}</span>
        )}
        <div className={`flex ${isWideScale ? "gap-0.5" : "gap-1"} flex-1 justify-center`}>
          {scaleValues.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => onChange(v)}
              className={`${isWideScale ? "w-7 h-8 text-xs" : "w-10 h-10"} rounded-lg font-medium transition-all ${
                value === v
                  ? colorClasses[color].selected
                  : colorClasses[color].unselected
              }`}
            >
              {v}
            </button>
          ))}
        </div>
        {!isWideScale && (
          <span className="text-xs text-gray-500 w-28">{highLabel}</span>
        )}
      </div>
    </div>
  );
}
